import React, { useCallback, useEffect, useState } from 'react';
import * as productAction from '../../redux/product/productAction';
import { useNavigate } from 'react-router-dom';
import {
  OptionI,
  ProductPageInterface,
  applicationTypeOptionInterface,
  paramsI
} from '../../../types/interface';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { t } from 'i18next';
import useAdvanceTable from 'hooks/useAdvanceTable';
import { Dropdown } from 'react-bootstrap';
import { capitalizeFirstLetter, checkAccess, showValue } from 'utils/helpers';
import SearchBox from 'components/common/SearchBox';
import { StandardButton } from 'base/StandardButton/StandardButton';
import { PRODUCT_FORM } from 'constant/RoutesUrls';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import CustomAdvanceTableFooter from 'components/customComponent/CustomAdvanceTableFooter';
import { RootState } from 'redux/rootReducer';
import { useSelector } from 'react-redux';
import { UilEllipsisV } from '@iconscout/react-unicons';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import { useAppDispatch } from '../../redux/HooksRedux';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import {
  ACCENDING,
  ARCHIVE,
  DESCENDING,
  modules,
  ViewAllLimit
} from 'utils/enums/misc';
import StandardSelect from 'base/StandardSelect/StandardSelect';
import {
  ApplicationTypes,
  archiveSearchOptions,
  debounceTime
} from 'utils/staticData';
import { StandardLoader } from 'base/Loader/Loader';
import Badge from 'components/base/Badge';
import SeeMoreText from 'components/base/SeeMoreText';
import { getApplicationTypeOptionAll } from '../../redux/applicationTypeOption/applicationTypeAction';

const Product = () => {
  const [productList, setProductList] = useState<ProductPageInterface[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  const [pageCount, setPageCount] = useState<number>(1);
  const [isAllVisibleData, setIsAllVisibleData] = useState(false);
  const [limit, setLimit] = useState<number>(10);
  const [archiveSearchValue, setArchiveSearchValue] = useState<string>('');
  const [selectedApplicationType, setSelectedApplicationType] = useState('');
  const [sorting, setSorting] = useState<SortingState>();
  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const [applicationTypeRequestOptions, setApplicationTypeRequestOptions] =
    useState<OptionI[]>([]);
  const [selectedRequestOption, setSelectedRequestOption] = useState<
    number | undefined
  >();

  const navigate = useNavigate();
  const [deleteProduct, setDeleteProduct] = useState<{
    product_id: number;
    showPopup: boolean;
  }>({
    product_id: 0,
    showPopup: false
  });
  const { totalPages, totalRecords } = useSelector(
    (state: RootState) => state?.productDataSlice || 0
  );
  const dispatch = useAppDispatch();

  const getAllProduct = async () => {
    setIsLoading(true);
    const params: paramsI & {
      requestOptions?: number;
      application_type: string;
    } = {
      page: pageCount,
      limit: isAllVisibleData ? ViewAllLimit : limit,
      keyword: searchText,
      is_archived: archiveSearchValue === ARCHIVE,
      sortBy: sorting?.[0]?.id,
      sortOrder: sorting?.[0]?.desc ? DESCENDING : ACCENDING,
      requestOptions: selectedRequestOption,
      application_type: selectedApplicationType
    };

    try {
      const response = await dispatch(productAction.getAllProductNew(params));
      if (response?.status === 200) {
        setProductList(response.data.data);
        if (pageCount > response?.data?.meta?.totalPages) {
          setPageCount(1);
        }
      } else {
        errorNotification(response.data.message);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAllApplicationTypeRequest = async () => {
    try {
      const response = await dispatch(
        getApplicationTypeOptionAll({
          limit: 999,
          page: 1,
          keyword: '',
          is_archived: false
        })
      );
      if (response?.status === 200) {
        setApplicationTypeRequestOptions(
          response.data.request_options.map(
            (item: applicationTypeOptionInterface) => {
              return {
                label: item.name,
                value: item.application_request_id
              };
            }
          )
        );
      } else {
        errorNotification(response.data.message);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const columns: ColumnDef<ProductPageInterface>[] = [
    {
      id: 'product_number',
      accessorKey: 'product_number',
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_29')}</span>
      ),
      cell: ({ row: { original } }) => (
        <div className="py-3">{showValue(original.product_number)}</div>
      )
    },
    {
      id: 'name',
      accessorKey: t('general.common_text.text_12'),
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_12')}</span>
      ),
      cell: ({ row: { original } }) => original.name
    },
    {
      id: 'application_type',
      accessorKey: t('product.table.title_1'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('product.table.title_1')}</span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <ConditionalRender
            condition={
              original?.application_type &&
              original?.application_type?.length > 0
            }
            fallBack={'--'}
          >
            {original.application_type?.map((item, index) => (
              <div key={`application-type-${index}`}>
                {ApplicationTypes?.find(a => a?.value === item)?.label || '--'}
              </div>
            ))}
          </ConditionalRender>
        );
      }
    },
    {
      id: 'request_options',
      accessorKey: t('product_form.placeholder.text_4'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">
          {t('product_form.placeholder.text_4')}
        </span>
      ),
      cell: ({ row: { original } }) => (
        <ConditionalRender
          condition={
            original.request_options && original.request_options.length > 0
          }
          fallBack={'--'}
        >
          {original.request_options.map(item => (
            <div key={item.id}>{item.name}</div>
          ))}
        </ConditionalRender>
      )
    },
    {
      id: 'description',
      accessorKey: t('product.table.title_4'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('product.table.title_4')}</span>
      ),
      cell: ({ row: { original } }) => (
        <SeeMoreText
          maxChars={38}
          link="#"
          as={'div'}
          inputType="text"
          headerText={t('product.table.title_4')}
        >
          {original.description || '--'}
        </SeeMoreText>
      )
    },
    {
      id: 'status',
      accessorKey: t('general.common_text.text_7'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('general.common_text.text_7')}</span>
      ),
      cell: ({ row: { original } }) => {
        return (
          <ConditionalRender
            condition={!original.is_archived}
            fallBack={
              <Badge variant="phoenix" bg="danger">
                {t('common_table.text_5')}
              </Badge>
            }
          >
            <Badge variant="phoenix" bg="success" className="py-1">
              {t('common_table.text_4')}
            </Badge>
          </ConditionalRender>
        );
      }
    },
    {
      id: 'action',
      accessorKey: checkAccess(
        [modules.PRODUCT.edit, modules.PRODUCT.delete],
        userPermission
      )
        ? t('general.common_text.text_8')
        : '',
      enableSorting: false,
      header: () =>
        checkAccess(
          [modules.PRODUCT.edit, modules.PRODUCT.delete],
          userPermission
        ) ? (
          <span className="text-nowrap">{t('general.common_text.text_8')}</span>
        ) : null,
      cell: ({ row: { original } }) => {
        return (
          <ConditionalRender
            condition={checkAccess(
              [modules.PRODUCT.edit, modules.PRODUCT.delete],
              userPermission
            )}
          >
            <Dropdown
              id="scroll_in_table"
              placement="auto"
              className="position-static"
            >
              <Dropdown.Toggle
                variant="link"
                className="no-padding no-margin ps-0 d-flex"
              >
                <UilEllipsisV className="text-body" />
              </Dropdown.Toggle>
              <Dropdown.Menu className="p-0">
                <ConditionalRender
                  condition={checkAccess(
                    [modules.PRODUCT.edit],
                    userPermission
                  )}
                >
                  <Dropdown.Item
                    className="pe-0 border-bottom"
                    onClick={() => {
                      navigate(
                        PRODUCT_FORM + `?productId=${original.product_id}`
                      );
                    }}
                  >
                    <span className="d-flex fs-16 p-0 font-normal">
                      <p className="mb-0">
                        {capitalizeFirstLetter(
                          t('general.common_text.text_13')
                        )}
                      </p>
                    </span>
                  </Dropdown.Item>
                </ConditionalRender>
                <ConditionalRender
                  condition={checkAccess(
                    [modules.PRODUCT.delete],
                    userPermission
                  )}
                >
                  <Dropdown.Item
                    onClick={() =>
                      setDeleteProduct({
                        product_id: original.product_id,
                        showPopup: true
                      })
                    }
                  >
                    <span className="d-flex fs-16 font-normal p-0">
                      <p className="mb-0">
                        {capitalizeFirstLetter(
                          t('general.common_text.text_14')
                        )}
                      </p>
                    </span>
                  </Dropdown.Item>
                </ConditionalRender>
              </Dropdown.Menu>
            </Dropdown>
          </ConditionalRender>
        );
      }
    }
  ];

  const debouncedSearch = useCallback(() => {
    const handler = setTimeout(() => {
      getAllProduct();
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [
    searchText,
    pageCount,
    isAllVisibleData,
    archiveSearchValue,
    sorting,
    limit,
    selectedRequestOption,
    selectedApplicationType
  ]);

  useEffect(() => {
    setIsLoading(true);
    const cleanup = debouncedSearch();
    return cleanup;
  }, [
    searchText,
    pageCount,
    isAllVisibleData,
    archiveSearchValue,
    selectedApplicationType,
    sorting,
    limit,
    selectedRequestOption
  ]);

  useEffect(() => {
    getAllApplicationTypeRequest();
  }, []);

  const table = useAdvanceTable({
    data: productList || [],
    columns: columns,
    selection: false,
    sortable: true,
    pageSize: limit,
    pagination: true,
    sorting: sorting,
    setSorting: setSorting
  });

  const handleDeleteProduct = async () => {
    try {
      const response = await dispatch(
        productAction.deleteProduct(deleteProduct.product_id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        setDeleteProduct({ product_id: 0, showPopup: false });
        getAllProduct();
      } else {
        errorNotification(response?.data?.message);
        setDeleteProduct({ product_id: 0, showPopup: false });
      }
    } catch (error) {
      setDeleteProduct({ product_id: 0, showPopup: false });
      console.log(error);
    }
  };

  const renderDeletePopup = () => {
    if (deleteProduct.showPopup) {
      return (
        <DeleteModel
          onCancel={() => setDeleteProduct({ product_id: 0, showPopup: false })}
          onConfirm={handleDeleteProduct}
        />
      );
    }
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between mb-3 align-items-center flex-column flex-md-row">
        <div className="w-auto">
          <h3>{capitalizeFirstLetter(t('header.text.text_3'))} </h3>
        </div>
        <div className="d-flex gap-2 w-auto flex-column flex-md-row flex-wrap">
          <SearchBox
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <StandardSelect
            formClassName="w-330"
            id=""
            label={''}
            onChange={(e: string | number) =>
              setSelectedRequestOption(e as number)
            }
            options={applicationTypeRequestOptions}
            placeholder={t('product.placeholder.text_1')}
            showError={false}
            validations={[]}
            value={selectedRequestOption}
            isClearable={true}
          />
          <StandardSelect
            value={selectedApplicationType}
            className="application_type"
            onChange={e => {
              setSelectedApplicationType(e);
            }}
            options={ApplicationTypes}
            placeholder={t('product.placeholder.text_3')}
            isClearable={true}
            isSearchable={false}
            isMulti={false}
            formClassName="w-330 "
            id=""
            label=""
            showError={false}
            validations={[]}
          />
          <StandardSelect
            value={archiveSearchValue}
            className="archive_search "
            onChange={e => {
              if (Array.isArray(e) && e.length === 0) {
                setArchiveSearchValue('');
              } else {
                setArchiveSearchValue(e);
              }
              setPageCount(1);
            }}
            options={archiveSearchOptions}
            placeholder={t('product.placeholder.text_2')}
            isClearable={true}
            isSearchable={false}
            isMulti={false}
            formClassName="w-330 "
            id=""
            label=""
            showError={false}
            validations={[]}
          />
          <ConditionalRender
            condition={checkAccess([modules.PRODUCT.create], userPermission)}
          >
            <StandardButton
              buttonText={capitalizeFirstLetter(
                t('general.common_text.text_17')
              )}
              onClick={() => {
                navigate(PRODUCT_FORM);
              }}
              className="h-100"
            />
          </ConditionalRender>
        </div>
      </div>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <div className="px-4 py-2 px-lg-6 bg-body-emphasis  rounded border-top border-bottom border-left border-right border position-relative top-1">
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table fs-9 p-0',
                responsive: true
              }}
              dataLength={productList.length}
            />
            <ConditionalRender condition={totalPages > 0}>
              <CustomAdvanceTableFooter
                pagination
                totalPages={totalPages}
                totalRecords={totalRecords}
                recordsPerPage={limit}
                currentPage={pageCount}
                setCurrentPage={setPageCount}
                CanPreviousPage={() => pageCount > 1}
                CanNextPage={() => totalPages > pageCount}
                handleNextPage={() => setPageCount(pageCount + 1)}
                handlePreviousPage={() => setPageCount(pageCount - 1)}
                showViewAllBtn={true}
                isAllVisibleData={isAllVisibleData}
                setIsAllVisibleData={setIsAllVisibleData}
                setRecordsPerPage={setLimit}
                isLoading={isLoading}
              />
            </ConditionalRender>
          </AdvanceTableProvider>
        </div>
      </ConditionalRender>
      {renderDeletePopup()}
    </>
  );
};

export default Product;
